import React, { JSX, useEffect, useState } from 'react';

import { api, AxiosError } from '@/services/biometricApi';
import { RekognitionSessionResponse } from '@/model/RekognitionSessionResponse';
import { RekognitionSessionCreateResponse } from '@/model/RekognitionSessionCreateResponse';
import { PostMessageRequest } from '@/model/PostMessageRequest';
import { LivenessError } from '@/model/LivenessError';
import { LivenessErrorContent } from '@/model/LivenessErrorContent';
import { CustomError } from '@/model/CustomError';
import { BiometricAuthResponse } from '@/model/BiometricAuthResponse';
import { BiometricAuthRequest } from '@/model/BiometricAuthRequest';
import { FaceContainer } from './ui';

interface StateProps {
  request: PostMessageRequest;
}

interface DispatchProps {
  onAnalysisComplete: (data: RekognitionSessionResponse) => void;
}

type Props = StateProps & DispatchProps;

export const FaceScreen: React.FC<Props> = ({ request, onAnalysisComplete }): JSX.Element => {
  const [biometricToken, setBiometricToken] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [sessionId, setSessionId] = useState<string>();
  const [livenessErrorContent, setLivenessErrorContent] = useState<LivenessErrorContent>();
  const [isAnalysisComplete, setIsAnalysisComplete] = useState<boolean>(false);
  const [screen, setScreen] = useState<number>(0);

  const authBiometric = async (): Promise<void> => {
    const payload: BiometricAuthRequest = {
      company: request.company,
      personIdentification: request.personIdentification,
      origin: request.origin,
      systemOS: request.systemOS,
      browser: request.browser as string,
    };
    const headers = {
      'Content-Type': 'application/json',
      'Api-Key': request.apiKey,
    };
    const { data } = await api.post<BiometricAuthResponse>('/auth/v1', payload, {
      headers,
    });
    setBiometricToken(data.token);
  };

  const fetchCreateLiveness = async (token: string): Promise<void> => {
    try {
      const { data } = await api.get<RekognitionSessionCreateResponse>('/rekognition/v1', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSessionId(data.sessionId);
      setLoading(false);
    } catch (error) {
      if (error instanceof AxiosError) {
        let message = '';
        const axiosError = error as AxiosError<CustomError, CustomError>;

        if (axiosError.response && axiosError.response.data) {
          if (axiosError.response.data.details && axiosError.response.data.details.length > 0) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < axiosError.response.data.details.length; i++) {
              if (i > 0) {
                break;
              } else {
                message = axiosError.response.data.details[i];
              }
            }
          } else if (axiosError.response.data.message) {
            message = axiosError.response.data.message;
          }
        }
        const newLivenessErrorContent: LivenessErrorContent = {
          title: 'Não foi possível confirmar sua prova de vida',
          message,
        };
        setSessionId(undefined);
        setLoading(false);
        setIsAnalysisComplete(false);
        setLivenessErrorContent(newLivenessErrorContent);
        setScreen(2);
      } else {
        setSessionId(undefined);
        setLoading(false);
        setIsAnalysisComplete(false);
        const newLivenessErrorContent: LivenessErrorContent = {
          title: 'Erro ao obter sessão',
          message: 'Ocorreu um erro ao obter a sessão, tente novamente mais tarde',
        };
        setLivenessErrorContent(newLivenessErrorContent);
        setScreen(2);
      }
    }
  };

  const nextScreen = async (): Promise<void> => {
    const newScreen = screen + 1;
    if (newScreen === 1 && biometricToken) {
      setScreen(newScreen);
      if (!sessionId) {
        fetchCreateLiveness(biometricToken);
      }
    }
  };

  const handleOnAnalysisComplete = async (): Promise<void> => {
    setIsAnalysisComplete(true);
  };

  const handleOnFetchSessionResult = async (): Promise<void> => {
    setLoading(true);
    const { data } = await api.get<RekognitionSessionResponse>(`/rekognition/v1/${sessionId}`, {
      headers: {
        Authorization: `Bearer ${biometricToken}`,
      },
    });

    if (data && data.confidence && data.confidence > 90) {
      setScreen(3);
      setTimeout(() => {
        onAnalysisComplete(data);
      }, 2000);
    } else {
      setSessionId(undefined);
      setLoading(false);
      setIsAnalysisComplete(false);
      const newLivenessErrorContent: LivenessErrorContent = {
        title: 'Não foi possível confirmar sua prova de vida',
        message: 'Sua pontuação biométrica é inferior a 90%',
      };
      setLivenessErrorContent(newLivenessErrorContent);
      setScreen(2);
    }
  };

  const handleOnError = async (livenessError: LivenessError): Promise<void> => {
    const newLivenessErrorContent: LivenessErrorContent = {
      title: 'Não foi possível confirmar sua prova de vida',
      message: '',
    };
    if (livenessError.state === 'CONNECTION_TIMEOUT') {
      newLivenessErrorContent.message =
        'O tempo limite expirou enquanto aguardava a correspondência do rosto.';
    } else if (livenessError.state === 'TIMEOUT') {
      newLivenessErrorContent.message =
        'O tempo limite expirou enquanto aguardava a correspondência do rosto.';
    } else if (livenessError.state === 'RUNTIME_ERROR') {
      newLivenessErrorContent.message = 'Não foi possível desenhar um oval.';
    } else if (livenessError.state === 'FRESHNESS_TIMEOUT') {
      newLivenessErrorContent.message =
        'Não foi possível concluir a validação de "atualização" dentro do tempo alocado. Isso pode ser devido a uma conexão de internet ruim ou problemas de desempenho com a câmera ou o dispositivo do usuário.';
    } else if (livenessError.state === 'SERVER_ERROR') {
      newLivenessErrorContent.message = livenessError.error.message;
    } else if (livenessError.state === 'CAMERA_FRAMERATE_ERROR') {
      newLivenessErrorContent.message =
        'O fluxo de vídeo da câmera não atende ao número mínimo de quadros por segundo (FPS) exigido.';
    } else if (livenessError.state === 'CAMERA_ACCESS_ERROR') {
      newLivenessErrorContent.message =
        'O aplicativo está tentando realizar uma verificação de atividade não consegue acessar a câmera do dispositivo. Esse erro geralmente surge de problemas relacionados às permissões ou à disponibilidade da câmera.';
    } else if (livenessError.state === 'FACE_DISTANCE_ERROR') {
      newLivenessErrorContent.message =
        'O rosto se encontra muito próximo da câmera antes ou durante a verificação de presença. Para resolver isso, precisa posicionar o rosto corretamente dentro do oval na tela.';
    } else if (livenessError.state === 'MOBILE_LANDSCAPE_ERROR') {
      newLivenessErrorContent.message =
        'A verificação falhou porque o dispositivo móvel estava na orientação paisagem.';
    } else if (livenessError.state === 'MULTIPLE_FACES_ERROR') {
      newLivenessErrorContent.message =
        'Este erro indica que o sistema identificou mais de um rosto no campo de visão da câmera durante a verificação de vivacidade.';
    }
    setSessionId(undefined);
    setLoading(false);
    setIsAnalysisComplete(false);
    setLivenessErrorContent(newLivenessErrorContent);
    setScreen(2);
    /*
    setLoading(true);
    if (postMessageRequest) {
      await authBiometric(postMessageRequest);
      setLoading(false);
      setScreen(0);
    }
    */
  };

  const backScreen = (): void => {
    if (screen > 0) {
      setSessionId(undefined);
      setScreen(0);
    }
  };

  useEffect(() => {
    if (isAnalysisComplete) {
      handleOnFetchSessionResult();
    }
  }, [isAnalysisComplete]);

  useEffect(() => {
    if (request) {
      authBiometric();
    }
  }, [request]);

  return (
    <div>
      <FaceContainer
        loading={loading}
        sessionId={sessionId}
        onAnalysisComplete={handleOnAnalysisComplete}
        onError={handleOnError}
        screen={screen}
        livenessErrorContent={livenessErrorContent}
        nextScreen={nextScreen}
        backScreen={backScreen}
        postMessageRequest={request}
      />
    </div>
  );
};
